/**
 * Field — labelled form-field wrapper.
 * Label is always above the input (good for mobile).
 * Passes through to any input element via children or via the `input` slot.
 */
import { forwardRef } from 'react';
import type { InputHTMLAttributes, SelectHTMLAttributes, ReactNode } from 'react';

const inputCls = (error?: string | null) =>
  [
    'w-full min-h-[44px] rounded-md border bg-surface px-3 py-2 text-body text-ink',
    'placeholder:text-ink-muted transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand',
    error
      ? 'border-neg focus:ring-neg/30 focus:border-neg'
      : 'border-border',
  ].join(' ');

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="w-full space-y-1.5">
      <label className="block text-small font-medium text-ink">
        {label}
        {required && <span className="ml-0.5 text-neg">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-caption text-ink-muted">{hint}</p>}
      {error && <p className="text-caption text-neg">{error}</p>}
    </div>
  );
}

/** Thin input that applies the standard field styling. */
export const FieldInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { error?: string | null }
>(({ error, className, ...props }, ref) => (
  <input ref={ref} className={[inputCls(error), className].filter(Boolean).join(' ')} {...props} />
));
FieldInput.displayName = 'FieldInput';

/** Select that applies the standard field styling. */
export const FieldSelect = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { error?: string | null }
>(({ error, className, children, ...props }, ref) => (
  <select ref={ref} className={[inputCls(error), className].filter(Boolean).join(' ')} {...props}>
    {children}
  </select>
));
FieldSelect.displayName = 'FieldSelect';

/** Textarea that applies the standard field styling. */
export const FieldTextarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: string | null }
>(({ error, className, ...props }, ref) => (
  <textarea ref={ref} className={[inputCls(error), 'resize-y', className].filter(Boolean).join(' ')} {...props} />
));
FieldTextarea.displayName = 'FieldTextarea';
